import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase"; // Adjust the path as needed

function SupervisorPasscodeManagerAutoClear() {
  const [doorNumber, setDoorNumber] = useState("");
  const [duration, setDuration] = useState(""); // in minutes
  const [result, setResult] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = useCallback((msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Call the new generate-and-auto-clear endpoint.
  const handleGeneratePasscode = useCallback(async () => {
    if (!doorNumber || !duration) {
      showSnackbar("Please fill in door number and duration.");
      return;
    }
    try {
      const generateFn = httpsCallable(functions, "generateAndSetSupervisorPasscodeWithAutoClear");
      const response = await generateFn({
        doorNumber: parseInt(doorNumber, 10),
        durationMinutes: parseInt(duration, 10),
      });
      setResult(JSON.stringify(response.data, null, 2));
      showSnackbar("Supervisor passcode generated, set, and will auto-clear.");
    } catch (error) {
      console.error("Error generating supervisor passcode:", error);
      showSnackbar(`Error: ${error.message}`);
    }
  }, [doorNumber, duration, showSnackbar]);

  // Manual clear option.
  const handleClearPasscode = useCallback(async () => {
    if (!doorNumber) {
      showSnackbar("Please enter a door number to clear passcodes.");
      return;
    }
    try {
      const clearFn = httpsCallable(functions, "clearSupervisorPasscodes");
      const response = await clearFn({ doorNumber: parseInt(doorNumber, 10) });
      setResult(JSON.stringify(response.data, null, 2));
      showSnackbar("Supervisor passcodes cleared.");
    } catch (error) {
      console.error("Error clearing supervisor passcodes:", error);
      showSnackbar(`Error: ${error.message}`);
    }
  }, [doorNumber, showSnackbar]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Supervisor Passcode Manager (Auto-Clear)
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Door Number"
              value={doorNumber}
              onChange={(e) => setDoorNumber(e.target.value)}
              fullWidth
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Duration (minutes)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              fullWidth
              type="number"
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleGeneratePasscode}>
              Generate & Set Supervisor Passcode (Auto-Clear)
            </Button>
            <Button variant="outlined" onClick={handleClearPasscode} sx={{ ml: 2 }}>
              Clear Passcode
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Result</Typography>
        <Box component="pre" sx={{ backgroundColor: "#f4f4f4", p: 2 }}>
          {result}
        </Box>
      </Paper>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="info" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SupervisorPasscodeManagerAutoClear;
